import { ComponentProps, ReactNode } from 'react'

import { AstraParagraph, astraTwMerge } from '~/astra'

type DivProps = ComponentProps<'div'>
const colorTypeDefinitions = {
    practitioner: '#D2DEFF',
    room: '#E6E9E8',
    room_conflict: '#fca5a5',
} as const
type ColorTypeKeys = keyof typeof colorTypeDefinitions

// This is workaround for allowing to pass any string but also give intellisense for predefined values
type ColorType = ColorTypeKeys | (string & { _?: never })

type TagProps = {
    fillType: 'none' | 'some' | 'all'
    whiteText?: boolean
    colorType: ColorType
    isLoading?: boolean
    label?: string
    smartTruncate?: boolean
    disableHover?: boolean
    endContent?: ReactNode
}

type Props = TagProps & DivProps

const baseStyles = 'flex items-center justify-center text-center w-full rounded disabled:pointer-events-none disabled:opacity-50'

export function getColorFromType(colorType: ColorType): string {
    return colorType in colorTypeDefinitions ? colorTypeDefinitions[colorType as ColorTypeKeys] : colorType
}

export const ButtonTag = ({
    colorType,
    smartTruncate = true,
    fillType,
    className,
    label,
    isLoading = false,
    style,
    whiteText = false,
    disableHover = false,
    endContent,
    ref,
    ...buttonProps
}: Props) => {
    const colorTypeValue = getColorFromType(colorType)

    const background = {
        none: '',
        some: `linear-gradient(165deg, ${colorTypeValue} 50%, white 50%)`,
        all: colorTypeValue,
    }

    const border = {
        none: `1px solid ${colorTypeValue}`,
        some: `1px solid ${colorTypeValue}`,
        all: '',
    }

    return (
        <div
            role="button"
            ref={ref}
            style={{ border: border[fillType], background: background[fillType], ...style }}
            className={astraTwMerge(
                baseStyles,
                isLoading ? 'animate-pulse' : '',
                buttonProps.onClick ? 'cursor-pointer hover:shadow-sm active:scale-95' : 'cursor-default',
                !disableHover ? 'cursor-pointer hover:shadow-sm' : 'cursor-default',
                className
            )}
            {...buttonProps}
        >
            <AstraParagraph
                className={`${smartTruncate ? 'hero-label' : ''} flex h-full w-full items-center justify-center overflow-hidden ${whiteText ? 'text-white' : 'text-heroBlack'}`}
            >
                {smartTruncate ? (
                    <>
                        <span className="full">{label}</span>
                        <span className="initial" aria-hidden="true">
                            {String(label)[0]}
                        </span>
                    </>
                ) : (
                    label
                )}
            </AstraParagraph>
            {endContent}
        </div>
    )
}
